/**
 * Kleine, DOM-unabhängige Helfer für den Seitenarbeitsbereich.
 *
 * Der Markdown-Text bleibt die Quelle. Für Navigation und Metadaten lesen wir
 * nur die sichtbaren Überschriften bzw. Wörter daraus; Codezäune werden zuvor
 * entfernt, damit ein Beispiel nicht versehentlich im Inhaltsverzeichnis
 * landet.
 */

export type SeitenUeberschrift = {
  ebene: 2 | 3;
  text: string;
  id: string;
};

export const MERMAID_VORLAGE = `\n\`\`\`mermaid\nflowchart LR\n  A[Ausgangspunkt] --> B[Ergebnis]\n\`\`\`\n`;

// rehype-sanitize schützt vor DOM-Clobbering und setzt dieses Präfix vor IDs.
const SICHERES_ANKER_PRAEFIX = "user-content-";

function sichtbarerUeberschriftstext(text: string): string {
  return text
    .replace(/\s+#+\s*$/, "")
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[*_~`]/g, "")
    .trim();
}

export function ueberschriftId(text: string): string {
  const id = text
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("de-DE")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return id || "abschnitt";
}

export function ueberschriftHref(textOderId: string): string {
  let roh = textOderId.replace(/^#/, "");
  try {
    roh = decodeURIComponent(roh);
  } catch {
    // Ungültige Prozentkodierung wird wie normaler Text sicher normalisiert.
  }
  if (roh.startsWith(SICHERES_ANKER_PRAEFIX)) return `#${roh}`;
  return `#${SICHERES_ANKER_PRAEFIX}${ueberschriftId(roh)}`;
}

export function erstelleUeberschriftSlugger() {
  const vorkommen = new Map<string, number>();
  return (text: string) => {
    const basis = ueberschriftId(text);
    const nummer = vorkommen.get(basis) ?? 0;
    vorkommen.set(basis, nummer + 1);
    return nummer === 0 ? basis : `${basis}-${nummer + 1}`;
  };
}

export function ueberschriftenAusMarkdown(
  markdown: string,
): SeitenUeberschrift[] {
  const ohneCode = markdown.replace(
    /(^|\n)[ \t]{0,3}(```|~~~)[^\n]*\n[\s\S]*?\n[ \t]{0,3}\2(?=\n|$)/g,
    "$1",
  );
  const slug = erstelleUeberschriftSlugger();
  const ergebnis: SeitenUeberschrift[] = [];
  for (const treffer of ohneCode.matchAll(/^(##|###)[ \t]+(.+)$/gm)) {
    const text = sichtbarerUeberschriftstext(treffer[2] ?? "");
    if (!text) continue;
    ergebnis.push({
      ebene: treffer[1] === "##" ? 2 : 3,
      text,
      id: slug(text),
    });
  }
  return ergebnis;
}

export function seitenTextStatistik(markdown: string): {
  woerter: number;
  leseminuten: number;
} {
  const text = markdown
    .replace(/(^|\n)[ \t]{0,3}(```|~~~)[^\n]*\n[\s\S]*?\n[ \t]{0,3}\2(?=\n|$)/g, " ")
    .replace(/https?:\/\/\S+/g, " ")
    .replace(/[:#>*_~`\[\](){}|!-]/g, " ");
  const woerter = text.match(/[\p{L}\p{N}]+(?:['’.-][\p{L}\p{N}]+)*/gu)?.length ?? 0;
  return {
    woerter,
    leseminuten: woerter === 0 ? 0 : Math.max(1, Math.ceil(woerter / 220)),
  };
}
